import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { WalkthroughStepType } from '@unifyapps/defs/types/page/walkthrough';
import type { WalkthroughSessionEntity } from '@unifyapps/defs/types/walkthroughSession';
import type { StoreApi, UseBoundStore } from 'zustand';
import { DEFAULT_CONDUCTOR_STATE } from './const';
import type {
  CreateWalkthroughManagerStoreParams,
  WalkthroughConductorSlice,
  WalkthroughManagerGetType,
  WalkthroughManagerSetType,
  WalkthroughManagerStore,
  WalkthroughProcessError,
} from './types';
import { WalkthroughProcessState } from './types';

export const createWalkthroughConductorSlice = (
  params: CreateWalkthroughManagerStoreParams,
  set: WalkthroughManagerSetType,
  get: WalkthroughManagerGetType,
) => {
  const { instanceId, device, userId, mode } = params;
  const slice: WalkthroughConductorSlice = {
    instanceId,
    device,
    userId,
    mode,
    walkthroughs: {},
    loadedPageIds: [],
    fetchingPageIds: [],
    fetchedPageIds: [],

    // walkthrough conductor States
    ...DEFAULT_CONDUCTOR_STATE,

    // process state tracking
    processState: WalkthroughProcessState.idle,
    processError: null,
    attemptCount: 0,
    lastTransitionTime: Date.now(),

    conductorActions: {
      /**
       * Set the page id when the page is loaded
       * @param pageId - The id of the page
       */
      setOnPageLoad: ({ pageId }: { pageId: string }) => {
        set((state) => {
          const newLoadedPageIds = Array.from([...state.loadedPageIds, pageId]);
          return {
            loadedPageIds: newLoadedPageIds,
          };
        });
      },

      /**
       * Set the page id when the page is unloaded
       * @param pageId - The id of the page
       */
      setOnPageUnload: ({ pageId }: { pageId: string }) => {
        set((state) => {
          return {
            loadedPageIds: state.loadedPageIds.filter((id) => id !== pageId),
          };
        });
      },

      /**
       * Start fetching walkthroughs for a page
       * @param pageId - The id of the page
       */
      startFetchingWalkthroughs: (pageId: string) => {
        set((state) => {
          return {
            fetchingPageIds: [...state.fetchingPageIds, pageId],
          };
        });
      },

      /**
       * Mark walkthroughs as fetched
       * @param pageId - The id of the page
       * @param walkthroughs - The walkthroughs to mark as fetched
       */
      markWalkthroughsFetched: (
        pageId: string,
        walkthroughs: Record<string, InterfacePageEntity>,
      ) => {
        set((state) => {
          const newFetchingPageIds = state.fetchingPageIds.filter((id) => id !== pageId);
          const newFetchedPageIds = [...state.fetchedPageIds, pageId];

          return {
            walkthroughs: {
              ...state.walkthroughs,
              ...walkthroughs,
            },
            fetchingPageIds: newFetchingPageIds,
            fetchedPageIds: newFetchedPageIds,
          };
        });
      },

      /**
       * Stop the walkthrough
       */
      stopWalkthrough: () => {
        get().conductorActions.destroy();
        get().driverActions.destroy();
      },

      transitionTo: (nextState: WalkthroughProcessState) => {
        set(() => {
          return {
            processState: nextState,
            lastTransitionTime: Date.now(),
          };
        });
      },

      setProcessError: (error: WalkthroughProcessError) => {
        set(() => ({
          processError: error,
          processState: WalkthroughProcessState.error,
        }));
      },

      /**
       * Prepare the walkthrough - setup driver with steps and active walkthrough id
       * @param walkthroughSteps - The steps of the walkthrough
       * @param walkthroughId - The id of the walkthrough
       */
      prepareWalkthrough: ({
        walkthroughSteps,
        walkthroughId,
      }: {
        walkthroughSteps: WalkthroughStepType[];
        walkthroughId: string;
      }) => {
        const state = get();

        // Set the active walkthrough id
        set({
          activeWalkthroughId: walkthroughId,
        });

        state.driverActions.setState('steps', walkthroughSteps);
      },

      /**
       * Activate the walkthrough - start the driver at the specified step (or first step if not provided) and transition to active state
       * @param initialStepIndex - Optional step index to start from (defaults to 0)
       */
      activateWalkthrough: (initialStepIndex?: number) => {
        const state = get();
        const activeWalkthroughId = state.activeWalkthroughId;

        if (!activeWalkthroughId) {
          state.conductorActions.setProcessError({
            code: 'NO_ACTIVE_WALKTHROUGH',
            message: 'No active walkthrough found when activating',
          });
          return;
        }

        if (state.processState !== WalkthroughProcessState.preparing_walkthrough) {
          state.conductorActions.setProcessError({
            code: 'INVALID_STATE',
            message: `Invalid state ${state.processState} when activating walkthrough`,
          });
          return;
        }

        // Validate initialStepIndex if provided
        const steps = state.driverState.steps;
        const stepIndex = initialStepIndex ?? 0;

        if (steps && stepIndex >= 0 && stepIndex < steps.length) {
          // Start the driver at the specified step
          state.driverActions.drive(stepIndex);
        } else if (stepIndex !== 0) {
          // Invalid step index, fallback to first step
          state.driverActions.drive(0);
        } else {
          // Start the driver at the first step
          state.driverActions.drive(0);
        }

        state.conductorActions.transitionTo(WalkthroughProcessState.walkthrough_active);
      },

      /**
       * Move to next/previous step
       * @param direction - The direction to move in
       */
      move: (direction: 'next' | 'previous') => {
        const state = get();

        if (state.processState !== WalkthroughProcessState.walkthrough_active) {
          console.error('Cannot move when walkthrough is not active');
          return;
        }

        state.driverActions.move(direction);
      },

      /**
       * Go to a specific step by index
       * @param stepIndex - The index of the step to go to
       */
      goToStep: (stepIndex: number) => {
        const state = get();

        if (state.processState !== WalkthroughProcessState.walkthrough_active) {
          console.error('Cannot go to step when walkthrough is not active');
          return;
        }

        const steps = state.driverState.steps;
        if (!steps || stepIndex < 0 || stepIndex >= steps.length) {
          console.error(`Invalid step index: ${stepIndex}`);
          return;
        }

        state.driverActions.drive(stepIndex);
      },

      /**
       * Destroy the walkthrough state
       */
      destroy: () => {
        set({
          processState: WalkthroughProcessState.idle,
          processError: null,
          attemptCount: 0,
          lastTransitionTime: Date.now(),
          ...DEFAULT_CONDUCTOR_STATE,
        });
      },

      setActiveSession: (
        updater:
          | ((session: WalkthroughSessionEntity | null) => WalkthroughSessionEntity | null)
          | WalkthroughSessionEntity
          | null,
      ) => {
        set((state) => ({
          activeSession: typeof updater === 'function' ? updater(state.activeSession) : updater,
        }));
      },
    },
  };

  return slice;
};

export const walkthroughConductorSelectors = (
  store: UseBoundStore<StoreApi<WalkthroughManagerStore>>,
) => ({
  useInstanceId: () => store((state) => state.instanceId),
  useDevice: () => store((state) => state.device),
  useMode: () => store((state) => state.mode),
  useActiveWalkthroughId: () => store((state) => state.activeWalkthroughId),
  useActiveSession: () => store((state) => state.activeSession),
  useSettings: () => store((state) => state.settings),
  useLoadedPageIds: () => store((state) => state.loadedPageIds),
  useConductorActions: () => store((state) => state.conductorActions),
  useProcessState: () => store((state) => state.processState),
  useProcessError: () => store((state) => state.processError),
  useFetchingPageIds: () => store((state) => state.fetchingPageIds),
  useFetchedPageIds: () => store((state) => state.fetchedPageIds),
  useIsInProcess: () =>
    store(
      (state) =>
        state.processState !== WalkthroughProcessState.idle &&
        state.processState !== WalkthroughProcessState.error,
    ),
  walkthroughs: {
    useDetails: (id: string) => store((state) => state.walkthroughs[id]),
    useAll: () => store((state) => state.walkthroughs),
    useByPageId: (pageId: string) =>
      store((state) =>
        Object.values(state.walkthroughs).filter(
          (walkthrough) =>
            walkthrough?.properties.walkthrough?.settings?.targetInterfacePageId === pageId,
        ),
      ),
  },
});
